import React, { useCallback } from 'react';
import Anchor from '@om-fe/core/components/anchor/anchor';
import { bemSlider } from '@om-fe/widgets/dist/components/slider/slider.constants';
import { BemModifier } from '@om-fe/utils';
import { RouteNames, ParamModel } from '@om-fe/core/core/routing/utils';
import { MixedGroupedTournamentModel } from '../mixed-grouped/mixed-grouped.types';
import { UseSimpleStore, useSimpleStoreItem } from '@om-fe/store';
import { FlagIcon } from '@om-fe/widgets/dist/components/icon/flag-icon';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { toURIPart } from '@om-fe/core/core/routing/provider';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import { MixedGroupedOddsOutrightsTabSelected } from '../mixed-grouped/components/tabs/mixed-grouped-odds-outrights-tabs.types';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { TournamentParams } from '@om-fe/models';

interface TournamentNavItemProps {
    readonly id: string;
    readonly useStore: UseSimpleStore<MixedGroupedTournamentModel>;
    readonly activeTab?: string;
    readonly handleTournamentParams?: (params: TournamentParams) => void;
    readonly tournamentParams: TournamentParams;
    readonly extraClassName?: string;
}

export interface RouteParams {
    readonly disciplineId: string;
    readonly disciplineName: string;
    readonly locationId?: string;
    readonly locationName?: string;
    readonly sportLocationId?: string;
    readonly sportLocationName?: string;
    readonly tournamentId: string;
    readonly tournamentName: string;
    readonly eventCategoryId: string;
    readonly eventCategoryName: string;
}

const config: OMFE2ConfigModel = getConfig();
const stableRoute: boolean | undefined = config?.stableRoute;

export const TournamentNavItem = (
    {
        id,
        useStore,
        handleTournamentParams,
        tournamentParams,
        activeTab: activeTabProps,
    }: TournamentNavItemProps) => {
        const {
            disciplineId,
            disciplineName,
            locationId,
            locationName,
            sportLocationId,
            sportLocationName,
            eventCategoryId,
            eventCategoryName,
            tournamentId,
            activeTab,
        } = tournamentParams;
        const { gettext } = useI18NContext();
       
        const handleClickAnchor = useCallback((routeParams: ParamModel) => {
            if (handleTournamentParams && tournamentParams && stableRoute) {
                handleTournamentParams({
                    ...tournamentParams,
                    ...routeParams,
                });
            }
        }, [handleTournamentParams, tournamentParams]);

        const tournament: MixedGroupedTournamentModel = useSimpleStoreItem<MixedGroupedTournamentModel>(useStore, id);

        if (id === 'all') {
            const routeParams: ParamModel = {
                locationName: (sportLocationName ?? locationName),
                locationId: (sportLocationId ?? locationId),
                disciplineName: disciplineName,
                disciplineId: disciplineId,
                tournamentName: gettext('all'),
                tournamentId: '0',
                activeTab: MixedGroupedOddsOutrightsTabSelected.Odds,
            };
            const routeParamsCategory: ParamModel = {
                eventCategoryName: eventCategoryName,
                eventCategoryId: eventCategoryId,
                disciplineName: disciplineName,
                disciplineId: disciplineId,
                tournamentName: gettext('all'),
                tournamentId: '0',
                activeTab: MixedGroupedOddsOutrightsTabSelected.Odds,
            };

            return (
                <Anchor
                    extraClassName={bemSlider('Action')}
                    activeClassName={(activeTab !== MixedGroupedOddsOutrightsTabSelected.Outrights) ? BemModifier.Active : ''}
                    routeName={typeof eventCategoryId !== 'undefined' ? RouteNames.tournamentCategories : RouteNames.tournamentLocation}
                    routeParams={typeof eventCategoryId !== 'undefined' ? routeParamsCategory : routeParams}
                    isActive={tournamentId === '0'}
                    preventRedirect={stableRoute ? true : false}
                    handleClickAnchor={() => handleClickAnchor(typeof eventCategoryId !== 'undefined' ? routeParamsCategory : routeParams)}
                >
                    <div className={bemSlider('ItemText')}>
                        {gettext('All')}
                    </div>
                </Anchor>
            );
        }
    
        if (id === MixedGroupedOddsOutrightsTabSelected.Outrights) {
            const routeParams: ParamModel = {
                locationName: (sportLocationName ?? locationName),
                locationId: (sportLocationId ?? locationId),
                disciplineName: disciplineName,
                disciplineId: disciplineId,
                tournamentName: gettext('all'),
                tournamentId: '0',
                activeTab: MixedGroupedOddsOutrightsTabSelected.Outrights,
            };
            
            const routeParamsCategory: ParamModel = {
                eventCategoryName: eventCategoryName,
                eventCategoryId: eventCategoryId,
                disciplineName: disciplineName,
                disciplineId: disciplineId,
                tournamentName: gettext('all'),
                tournamentId: '0',
                activeTab: MixedGroupedOddsOutrightsTabSelected.Outrights,
            };

            const newActiveTab: string | undefined = activeTab || activeTabProps;

            return (
                <Anchor
                    extraClassName={bemSlider('Action')}
                    activeClassName={BemModifier.Active}
                    routeName={typeof eventCategoryId !== 'undefined' ? RouteNames.tournamentCategories : RouteNames.tournamentLocation}
                    routeParams={typeof eventCategoryId !== 'undefined' ? routeParamsCategory : routeParams}
                    isActive={newActiveTab === MixedGroupedOddsOutrightsTabSelected.Outrights}
                    preventRedirect={stableRoute ? true : false}
                    handleClickAnchor={() => handleClickAnchor(typeof eventCategoryId !== 'undefined' ? routeParamsCategory : routeParams)}
                >
                    <div className={bemSlider('ItemIcon')}>
                        <GeneralIcon name={'cup-alt'} className={bemSlider('Icon')} />
                    </div>
                    <div className={bemSlider('ItemText')}>
                        {gettext('Outrights')}
                    </div>
                </Anchor>
            );
        }
    
        const routeParamsTournamentLocation: ParamModel = {
            locationName: (sportLocationName ?? locationName),
            locationId: (sportLocationId ?? locationId),
            disciplineName: disciplineName,
            disciplineId: disciplineId,
            tournamentName: toURIPart(tournament.name),
            tournamentId: tournament.id,
            activeTab: MixedGroupedOddsOutrightsTabSelected.Odds,
        };
    
        const routeParamsTournamentCategories: ParamModel = {
            eventCategoryId: tournament.categoryId,
            eventCategoryName: tournament.categoryName,
            disciplineName: disciplineName,
            disciplineId: disciplineId,
            tournamentName: toURIPart(tournament.name),
            tournamentId: tournament.id,
            activeTab: MixedGroupedOddsOutrightsTabSelected.Odds,
        };
    
        return (
            <Anchor
                extraClassName={bemSlider('Action')}
                activeClassName={BemModifier.Active}
                routeName={typeof eventCategoryId !== 'undefined' ? RouteNames.tournamentCategories : RouteNames.tournamentLocation}
                routeParams={typeof eventCategoryId !== 'undefined' ? routeParamsTournamentCategories : routeParamsTournamentLocation}
                isActive={tournament.id === tournamentId}
                preventRedirect={stableRoute ? true : false}
                handleClickAnchor={() => handleClickAnchor(typeof eventCategoryId !== 'undefined' ? routeParamsTournamentCategories : routeParamsTournamentLocation)}
            >
                {tournament.locationId && (
                    <div className={bemSlider('ItemIcon')}>
                        <FlagIcon locationId={tournament.locationId} />
                    </div>
                )}
                <div className={bemSlider('ItemText')}>
                    {tournament.shortTranslatedName ?? tournament.translatedName}
                </div>
            </Anchor>
        );
    };